import { Component, EventEmitter, Inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, mergeMap, take } from 'rxjs/operators';
import { CONNECTION_ERROR_CODES } from '../../enums/connection-error-codes.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { NotificationModel } from '../../models/low-level/notification.model';
import { AppNotificationType } from '../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../services/app-notification/app-notification.service';
import { OdataEscalationService } from '../../services/escalation/odata-escalation.service';
import { NotificationService } from '../../services/notification/notification.service';
import { EscalationModel } from './escalation.model';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

@Component({
    selector: 'pace-core-escalation',
    templateUrl: './escalation.component.html',
    styleUrls: ['./escalation.component.scss'],
})
export class EscalationComponent implements OnInit {
    @Input()
    public notification: NotificationModel;

    public model: EscalationModel;
    public formChildren: FormArray;
    public escalationForm: FormGroup;
    public isLoading = false;
    public modalResult = new EventEmitter<boolean>();
    public escalationMessage: string;
    public notificationDetails: string;
    public constructor(
        private readonly translate: TranslateService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly modalInstance: BsModalRef,
        private readonly notificationService: NotificationService,
        private readonly odataEscalationService: OdataEscalationService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        private readonly appNotificationService: AppNotificationService,
        private readonly formBuilder: FormBuilder,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.escalationForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
        this.model = new EscalationModel(this.notification.identifier);

        const osrStatus = this.notificationService.getStatus(this.notification);

        const diffTimeString = osrStatus.startTime.fromNow();

        this.notificationDetails = <string>this.translate.instant('ESCALATION_NOTIFICATION_DETAIL', {
            notificationNo: this.notification.identifier.qmNum,
            customerName: this.notification.address.name1.value,
        });

        this.escalationMessage = <string>this.translate.instant('ESCALATION_TIMEOUT_TEXT', {
            timeDiff: diffTimeString,
        });
    }

    /**
     * This function sends the request to the backend for escalation
     */
    public escalateToRSC(): void {
        this.isLoading = true;
        this.odataEscalationService
            .saveEscalationStatus(this.model)
            .pipe(
                take(1),
                mergeMap(() => this.notificationService.saveEscalated(this.notification, true)),
                mergeMap(() => {
                    this.modalResult.next(true);
                    this.modalResult.complete();
                    this.modalInstance.hide();
                    this.isLoading = false;

                    this.log.debug('escalateToRSC -> sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error('escalateToRSC - error' + JSON.stringify(error, Object.getOwnPropertyNames(error)));
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                            : <string>this.translate.instant('ESCALATION_SUBMIT_ERROR_TITLE'),
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                            : <string>this.translate.instant('ESCALATION_SUBMIT_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }
}
